/**
 * Filtros do setor: um botão discreto que abre o painel completo (montado a
 * partir de DEFINICOES_FILTROS, agrupado em Onde / Quem / Ambiente /
 * Situação / Prazo) e a fileira de chips do que está valendo, cada um com
 * "x". A busca livre fica na barra; o filtro de setor não se aplica aqui.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiSliders, FiX } from "react-icons/fi";
import styled, { keyframes } from "styled-components";

import { mq } from "../../../styles/canonical/breakpoints";
import { normalizeText } from "../../../utils/data";
import { plural } from "../../../utils/dinheiro";
import {
  DEFINICOES_FILTROS,
  GRUPOS_FILTROS,
  opcoesDoFiltro,
  resumoDosFiltros,
} from "../dados/filtros";
import { BotaoBarra, Chip, Chips, Contador } from "./styles";

const OCULTOS = new Set(["busca", "setores"]);
const OPCOES_COM_BUSCA = 8;

const aparecer = keyframes`
  from { opacity: 0; transform: scale(0.97) translateY(-4px); }
  to { opacity: 1; transform: none; }
`;

const Ancora = styled.div`
  position: relative;
`;

const Painel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 40;
  width: min(820px, calc(100vw - 32px));
  max-height: min(72vh, 680px);
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow-3);
  transform-origin: top right;
  animation: ${aparecer} 160ms cubic-bezier(0.23, 1, 0.32, 1);
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @media ${mq.mobile} {
    position: fixed;
    top: auto;
    left: 16px;
    right: 16px;
    bottom: 16px;
    width: auto;
  }

  > .corpo {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 18px 20px;
  }
  > .rodape {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 10px 16px;
    border-top: 1px solid var(--border);
    background: var(--surface-2);
    border-radius: 0 0 14px 14px;
    font-size: 12px;
    color: var(--ink-3);
  }
  > .rodape .espaco {
    flex: 1 1 auto;
  }
`;

const GrupoFiltros = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  > legend {
    padding: 0;
    margin-bottom: 4px;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-3);

    @media ${mq.tablet} {
      font-size: var(--text-min, 12px);
    }
  }
`;

const Filtro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  > .cabeca {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-2);
  }
  > .cabeca .n {
    font-family: var(--font-mono);
    font-weight: 500;
    color: var(--accent);
  }
  > .cabeca .limpar {
    margin-left: auto;
    border: 0;
    background: none;
    padding: 0;
    color: var(--ink-3);
    font: inherit;
    font-size: 11px;
    cursor: pointer;

    @media ${mq.tablet} {
      min-height: 36px;
      padding: 0 6px;
      font-size: var(--text-min, 12px);
    }
  }
  > .cabeca .limpar:hover {
    color: var(--ink);
  }
  input[type="search"],
  input[type="date"] {
    min-width: 0;
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: 12px;
  }
  input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: var(--shadow-focus);
  }
  > .periodo {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
`;

const ListaOpcoes = styled.div`
  max-height: 168px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 4px;

  > label {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    padding: 0 8px;
    border-radius: 6px;
    font-size: 12.5px;
    color: var(--ink);
    cursor: pointer;
  }
  > label:hover {
    background: var(--surface-hover);
  }
  > label .inativo {
    color: var(--ink-4);
  }
  > .vazio {
    padding: 8px;
    font-size: 12px;
    color: var(--ink-4);
  }
  input[type="checkbox"] {
    accent-color: var(--accent);
    width: 16px;
    height: 16px;
  }
`;

const Opcoes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  > button {
    min-height: 32px;
    padding: 0 10px;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--ink-2);
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }
  > button[aria-pressed="true"] {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--accent);
  }
`;

function FiltroMultiplo({ definicao, valor, opcoes, onChange }) {
  const [busca, setBusca] = useState("");
  const selecionados = new Set(valor);
  const alvo = normalizeText(busca);
  // Marcados primeiro: o que está valendo não some numa lista longa.
  const visiveis = opcoes
    .filter((o) => !alvo || normalizeText(o.rotulo).includes(alvo))
    .sort((a, b) => Number(selecionados.has(b.valor)) - Number(selecionados.has(a.valor)));
  const alternar = (v) => {
    const proximo = new Set(selecionados);
    if (proximo.has(v)) proximo.delete(v);
    else proximo.add(v);
    onChange([...proximo]);
  };
  return (
    <Filtro>
      <div className="cabeca">
        <span>{definicao.rotulo}</span>
        {valor.length > 0 && <span className="n">{valor.length}</span>}
        {valor.length > 0 && (
          <button type="button" className="limpar" onClick={() => onChange([])}>
            Limpar
          </button>
        )}
      </div>
      {opcoes.length > OPCOES_COM_BUSCA && (
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={`Buscar ${definicao.rotulo.toLowerCase()}`}
          aria-label={`Buscar ${definicao.rotulo.toLowerCase()}`}
        />
      )}
      <ListaOpcoes role="group" aria-label={definicao.rotulo}>
        {visiveis.length === 0 && <span className="vazio">Nada para escolher.</span>}
        {visiveis.map((o) => (
          <label key={o.valor}>
            <input type="checkbox" checked={selecionados.has(o.valor)} onChange={() => alternar(o.valor)} />
            <span>
              {o.rotulo}
              {o.inativo && <span className="inativo"> (desativado)</span>}
            </span>
          </label>
        ))}
      </ListaOpcoes>
    </Filtro>
  );
}

function FiltroOpcao({ definicao, valor, onChange }) {
  return (
    <Filtro>
      <div className="cabeca">
        <span>{definicao.rotulo}</span>
      </div>
      <Opcoes role="group" aria-label={definicao.rotulo}>
        <button type="button" aria-pressed={!valor} onClick={() => onChange("")}>
          Todos
        </button>
        {definicao.opcoes.map((o) => (
          <button key={o.valor} type="button" aria-pressed={valor === o.valor} onClick={() => onChange(o.valor)}>
            {o.rotulo}
          </button>
        ))}
      </Opcoes>
    </Filtro>
  );
}

function FiltroPeriodo({ definicao, valor, onChange }) {
  return (
    <Filtro>
      <div className="cabeca">
        <span>{definicao.rotulo}</span>
        {(valor.de || valor.ate) && (
          <button type="button" className="limpar" onClick={() => onChange({ de: "", ate: "" })}>
            Limpar
          </button>
        )}
      </div>
      <div className="periodo">
        <input
          type="date"
          aria-label={`${definicao.rotulo}: de`}
          value={valor.de}
          onChange={(e) => onChange({ ...valor, de: e.target.value })}
        />
        <input
          type="date"
          aria-label={`${definicao.rotulo}: até`}
          value={valor.ate}
          onChange={(e) => onChange({ ...valor, ate: e.target.value })}
        />
      </div>
    </Filtro>
  );
}

function useFecharAoClicarFora(aberto, ref, fechar) {
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) fechar();
    };
    const esc = (e) => {
      if (e.key === "Escape") fechar();
    };
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto, ref, fechar]);
}

/** Quantos filtros do painel estão valendo (sem busca e setor). */
export function contarFiltrosDoPainel(filtros) {
  return resumoDosFiltros(filtros).filter((c) => !OCULTOS.has(c.id)).length;
}

export default function PainelFiltros({ filtros, definirFiltro, onLimpar, contexto, resultado }) {
  const [aberto, setAberto] = useState(false);
  const ancoraRef = useRef(null);
  const fechar = useCallback(() => setAberto(false), []);
  useFecharAoClicarFora(aberto, ancoraRef, fechar);
  const total = contarFiltrosDoPainel(filtros);

  const porGrupo = GRUPOS_FILTROS.map((grupo) => ({
    ...grupo,
    definicoes: DEFINICOES_FILTROS.filter((d) => d.grupo === grupo.id && !OCULTOS.has(d.id)),
  })).filter((g) => g.definicoes.length);

  return (
    <Ancora ref={ancoraRef}>
      <BotaoBarra
        type="button"
        $ativo={total > 0}
        aria-expanded={aberto}
        aria-haspopup="dialog"
        onClick={() => setAberto((v) => !v)}
      >
        <FiSliders size={14} aria-hidden="true" />
        Filtros
        {total > 0 && <Contador aria-label={`${total} ativos`}>{total}</Contador>}
      </BotaoBarra>
      {aberto && (
        <Painel role="dialog" aria-label="Filtros do setor">
          <div className="corpo">
            {porGrupo.map((grupo) => (
              <GrupoFiltros key={grupo.id}>
                <legend>{grupo.rotulo}</legend>
                {grupo.definicoes.map((d) => {
                  const valor = filtros[d.id];
                  const mudar = (novo) => definirFiltro(d.id, novo);
                  if (d.tipo === "multipla") {
                    return (
                      <FiltroMultiplo
                        key={d.id}
                        definicao={d}
                        valor={valor}
                        opcoes={opcoesDoFiltro(d.id, contexto)}
                        onChange={mudar}
                      />
                    );
                  }
                  if (d.tipo === "opcao") return <FiltroOpcao key={d.id} definicao={d} valor={valor} onChange={mudar} />;
                  return <FiltroPeriodo key={d.id} definicao={d} valor={valor} onChange={mudar} />;
                })}
              </GrupoFiltros>
            ))}
          </div>
          <div className="rodape">
            <span>
              {plural(resultado.ambientes, "ambiente", "ambientes")} · {plural(resultado.tarefas, "tarefa", "tarefas")}{" · "}
              {plural(resultado.pecas || 0, "peça", "peças")}
            </span>
            <span className="espaco" />
            {total > 0 && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={onLimpar}>
                Limpar filtros
              </button>
            )}
            <button type="button" className="btn btn--secondary btn--sm" onClick={fechar}>
              Fechar
            </button>
          </div>
        </Painel>
      )}
    </Ancora>
  );
}

/** Chips do que está filtrando, cada um com "x" para tirar. */
export function ChipsDosFiltros({ filtros, contexto, onRemover, onLimpar }) {
  const chips = resumoDosFiltros(filtros, contexto).filter((c) => !OCULTOS.has(c.id));
  if (!chips.length) return null;
  return (
    <Chips aria-label="Filtros ativos">
      {chips.map((c) => (
        <Chip
          key={c.id}
          type="button"
          $ativo
          onClick={() => onRemover(c.id)}
          title={`Remover filtro ${c.rotulo}`}
          aria-label={`Remover filtro ${c.rotulo}: ${c.valor}`}
        >
          <span className="rotulo">{c.rotulo}:</span>
          <span className="texto">{c.valor}</span>
          <FiX size={12} aria-hidden="true" />
        </Chip>
      ))}
      {chips.length > 1 && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={onLimpar}>
          Limpar tudo
        </button>
      )}
    </Chips>
  );
}
